import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!project) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Eye' },
    { id: 'process', label: 'Process', icon: 'Workflow' },
    { id: 'technical', label: 'Technical', icon: 'Code' },
    { id: 'results', label: 'Results', icon: 'TrendingUp' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project?.gallery?.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project?.gallery?.length - 1 : prev - 1
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-background/90 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Content */}
          <motion.div
            className="relative w-full max-w-6xl max-h-[90vh] glass-panel rounded-2xl shadow-cosmic-lg overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border/20">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  project?.category === 'Web Development' ?'bg-accent/20 text-accent'
                    : project?.category === 'Creative Coding' ?'bg-cosmic-energy/20 text-cosmic-energy' :'bg-cosmic-cta/20 text-cosmic-cta'
                }`}>
                  <Icon 
                    name={project?.category === 'Web Development' ? 'Monitor' : 
                          project?.category === 'Creative Coding' ? 'Zap' : 'Megaphone'} 
                    size={24} 
                  />
                </div>
                <div>
                  <h2 className="font-headline font-bold text-2xl text-foreground">
                    {project?.title}
                  </h2>
                  <p className="font-body text-muted-foreground">
                    {project?.client} • {project?.year}
                  </p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                iconName="X"
                iconSize={20}
                className="text-muted-foreground hover:text-foreground"
              />
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center space-x-1 px-6 py-4 border-b border-border/20">
              {tabs?.map((tab) => (
                <Button
                  key={tab?.id}
                  variant={activeTab === tab?.id ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab(tab?.id)}
                  iconName={tab?.icon}
                  iconPosition="left"
                  iconSize={16}
                  className={`${
                    activeTab === tab?.id
                      ? 'bg-accent text-background' :'text-muted-foreground hover:text-foreground'
                  } transition-all duration-200`}
                >
                  {tab?.label}
                </Button>
              ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto max-h-[60vh]">
              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Image Gallery */}
                    <div className="space-y-4">
                      <div className="relative aspect-video rounded-xl overflow-hidden">
                        <Image
                          src={project?.gallery?.[currentImageIndex]}
                          alt={`${project?.title} - Image ${currentImageIndex + 1}`}
                          className="w-full h-full object-cover"
                        />
                        
                        {project?.gallery?.length > 1 && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={prevImage}
                              iconName="ChevronLeft"
                              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-background/80 hover:bg-background/90"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={nextImage}
                              iconName="ChevronRight"
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-background/80 hover:bg-background/90"
                            />
                          </>
                        )}
                      </div>

                      {/* Thumbnail Strip */}
                      {project?.gallery?.length > 1 && (
                        <div className="flex space-x-2 overflow-x-auto">
                          {project?.gallery?.map((img, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                                idx === currentImageIndex
                                  ? 'border-accent' :'border-transparent hover:border-border'
                              }`}
                            >
                              <Image
                                src={img}
                                alt={`Thumbnail ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Project Details */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-headline font-bold text-lg text-foreground mb-3">
                          Project Overview
                        </h3>
                        <p className="font-body text-muted-foreground leading-relaxed">
                          {project?.fullDescription}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="glass-panel-light rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon name="User" size={16} className="text-accent" />
                            <span className="font-body font-medium text-sm text-foreground">Role</span>
                          </div>
                          <p className="text-muted-foreground text-sm">{project?.role}</p>
                        </div>

                        <div className="glass-panel-light rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon name="Clock" size={16} className="text-accent" />
                            <span className="font-body font-medium text-sm text-foreground">Duration</span>
                          </div>
                          <p className="text-muted-foreground text-sm">{project?.duration}</p>
                        </div>

                        <div className="glass-panel-light rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon name="Target" size={16} className="text-accent" />
                            <span className="font-body font-medium text-sm text-foreground">Impact</span>
                          </div>
                          <p className="text-accent text-sm font-medium">{project?.impact}</p>
                        </div>

                        <div className="glass-panel-light rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Icon name="Star" size={16} className="text-accent" />
                            <span className="font-body font-medium text-sm text-foreground">Rating</span>
                          </div>
                          <p className="text-muted-foreground text-sm">{project?.rating}/5.0</p>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-body font-medium text-foreground mb-3">Technologies Used</h4>
                        <div className="flex flex-wrap gap-2">
                          {project?.technologies?.map((tech, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-surface/50 text-muted-foreground rounded-full text-sm border border-border/20"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'process' && (
                  <div className="space-y-6">
                    <h3 className="font-headline font-bold text-lg text-foreground">
                      Development Process
                    </h3>
                    <div className="space-y-4">
                      {project?.processSteps?.map((step, idx) => (
                        <div key={idx} className="flex space-x-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-accent/20 text-accent rounded-full flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-body font-medium text-foreground mb-1">{step?.title}</h4>
                            <p className="text-muted-foreground text-sm">{step?.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'technical' && (
                  <div className="space-y-6">
                    <h3 className="font-headline font-bold text-lg text-foreground">
                      Technical Implementation
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="glass-panel-light rounded-lg p-4">
                        <h4 className="font-body font-medium text-foreground mb-3">Architecture</h4>
                        <p className="text-muted-foreground text-sm">{project?.architecture}</p>
                      </div>
                      <div className="glass-panel-light rounded-lg p-4">
                        <h4 className="font-body font-medium text-foreground mb-3">Challenges</h4>
                        <p className="text-muted-foreground text-sm">{project?.challenges}</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'results' && (
                  <div className="space-y-6">
                    <h3 className="font-headline font-bold text-lg text-foreground">
                      Project Results
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {project?.metrics?.map((metric, idx) => (
                        <div key={idx} className="glass-panel-light rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-accent mb-1">{metric?.value}</div>
                          <div className="text-sm text-muted-foreground">{metric?.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between p-6 border-t border-border/20">
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  iconName="ExternalLink"
                  iconPosition="right"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  View Live Site
                </Button>
                <Button
                  variant="ghost"
                  iconName="Github"
                  iconPosition="left"
                  className="text-muted-foreground hover:text-foreground"
                >
                  Source Code
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  iconName="Share"
                  iconSize={16}
                  className="text-muted-foreground hover:text-foreground"
                  title="Share Project"
                />
                <Button
                  variant="ghost"
                  iconName="Heart"
                  iconSize={16}
                  className="text-muted-foreground hover:text-cosmic-cta"
                  title="Like Project"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;